import { devLog } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import {
  ArrowLeft, DollarSign, TrendingUp, Clock, CheckCircle, Loader2,
  AlertCircle, ArrowDownToLine
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-[#D4A373]/10 text-[#D4A373]', icon: Clock },
  processing: { label: 'Processando', color: 'bg-[#4A5D4E]/10 text-[#4A5D4E]', icon: Loader2 },
  completed: { label: 'Concluído', color: 'bg-[#6B8E7C]/10 text-[#6B8E7C]', icon: CheckCircle },
  failed: { label: 'Falhou', color: 'bg-[#B85C5C]/10 text-[#B85C5C]', icon: AlertCircle }
};

export default function WithdrawalsPage() {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [earningsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/professional/earnings`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/professional/withdrawals`, { headers, withCredentials: true })
      ]);
      setEarnings(earningsRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch (err) {
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('Informe um valor válido'); return; }
    if (val > (earnings?.current_balance || 0)) { toast.error('Saldo insuficiente'); return; }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/professional/withdraw`,
        { amount: val },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Saque de R$ ${val.toFixed(2)} solicitado com sucesso!`);
      setAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar saque');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" />
        </main>
      </div>
    );
  }

  const balance = earnings?.current_balance || 0;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="withdrawals-page">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-8">
            <ArrowLeft className="w-4 h-4" /> Voltar ao Dashboard
          </Link>

          <h1 className="text-3xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Saque de Ganhos
          </h1>
          <p className="text-[#63635E] mb-8">Solicite o saque dos seus ganhos de sessões concluídas</p>

          {/* Balance Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="card-theraflow p-5">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-[#6B8E7C]" strokeWidth={1.5} />
                <span className="text-sm text-[#63635E]">Saldo Disponível</span>
              </div>
              <p className="text-2xl font-light text-[#292927]" data-testid="available-balance">
                R$ {balance.toFixed(2)}
              </p>
            </div>
            <div className="card-theraflow p-5">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
                <span className="text-sm text-[#63635E]">Total Ganhos</span>
              </div>
              <p className="text-2xl font-light text-[#292927]">
                R$ {(earnings?.total_earnings || 0).toFixed(2)}
              </p>
            </div>
            <div className="card-theraflow p-5">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
                <span className="text-sm text-[#63635E]">Sessões Concluídas</span>
              </div>
              <p className="text-2xl font-light text-[#292927]">
                {earnings?.completed_sessions_count || 0}
              </p>
            </div>
          </div>

          {/* Withdraw Form */}
          <div className="card-theraflow p-6 mb-8" data-testid="withdraw-form">
            <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Solicitar Saque
            </h2>
            {balance > 0 ? (
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-sm text-[#63635E]">Valor do saque (R$)</Label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    min="1"
                    max={balance}
                    step="0.01"
                    placeholder={`Máx: ${balance.toFixed(2)}`}
                    className="input-theraflow mt-1"
                    data-testid="withdraw-amount"
                  />
                </div>
                <button
                  onClick={() => setAmount(balance.toFixed(2))}
                  className="btn-secondary py-2.5 text-sm"
                >
                  Sacar tudo
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={submitting || !amount}
                  className="btn-primary flex items-center gap-2"
                  data-testid="withdraw-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowDownToLine className="w-4 h-4" /> Solicitar</>}
                </button>
              </div>
            ) : (
              <div className="text-center py-6">
                <DollarSign className="w-12 h-12 mx-auto text-[#A0A09A] mb-3" strokeWidth={1} />
                <p className="text-[#63635E]">Nenhum saldo disponível para saque</p>
                <p className="text-sm text-[#A0A09A] mt-1">Complete sessões para acumular ganhos</p>
              </div>
            )}
            <p className="text-xs text-[#A0A09A] mt-3">
              O saque é processado em até 3 dias úteis após a solicitação
            </p>
          </div>

          {/* Withdrawal History */}
          <div className="card-theraflow p-6" data-testid="withdrawal-history">
            <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Histórico de Saques
            </h2>
            {withdrawals.length === 0 ? (
              <p className="text-center text-[#A0A09A] py-6">Nenhum saque realizado ainda</p>
            ) : (
              <div className="space-y-3">
                {withdrawals.map((w) => {
                  const cfg = statusConfig[w.status] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={w.withdrawal_id} className="flex items-center justify-between p-4 rounded-xl bg-[#F9F8F6]" data-testid={`withdrawal-${w.withdrawal_id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${cfg.color} flex items-center justify-center`}>
                          <StatusIcon className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-medium text-[#292927]">R$ {w.amount.toFixed(2)}</p>
                          <p className="text-xs text-[#A0A09A]">
                            {new Date(w.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
